import { type JSX } from 'react'
import type {
  PendingWhatsAppMessage,
  WhatsAppCall,
  WhatsAppChatMessage,
} from '../../hooks/useWhatsAppChats.js'
import {
  Check,
  CheckCheck,
  AlertCircle,
  Download,
  FileText,
  Loader2,
  X,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
} from 'lucide-react'
import { formatInTimezone } from '../../utils/timezone.js'

type Status = WhatsAppChatMessage['status'] | 'sending'

function MessageTicks({ status }: { status: Status }): JSX.Element | null {
  switch (status) {
    case 'sending':
      return <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
    case 'sent':
      return <Check className="h-3 w-3 text-gray-400" />
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-gray-400" />
    case 'read':
      return <CheckCheck className="h-3 w-3 text-[#53BDEB]" />
    case 'failed':
      return <AlertCircle className="h-3 w-3 text-red-500" />
    default:
      return null
  }
}

function MediaBody({ chatId, msg }: { chatId: string; msg: WhatsAppChatMessage }): JSX.Element | null {
  if (!msg.media_id) return null
  // Meta media needs the business token, so it is streamed through the API.
  const src = `/api/agent/whatsapp-chats/${chatId}/media/${msg.media_id}`
  switch (msg.message_type) {
    case 'image':
    case 'sticker':
      return (
        <img
          src={src}
          alt={msg.content_text ?? 'Image'}
          className="mb-1 max-h-64 w-full rounded-lg object-cover"
        />
      )
    case 'video':
      return <video src={src} controls className="mb-1 max-h-64 w-full rounded-lg" />
    case 'audio':
      return <audio src={src} controls className="mb-1 w-56" />
    case 'document':
      return (
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="mb-1 flex items-center gap-2 rounded-lg bg-black/5 px-2 py-1.5 text-xs hover:bg-black/10"
        >
          <Download className="h-3.5 w-3.5" />
          <span className="truncate">{msg.media_filename ?? 'Document'}</span>
        </a>
      )
    default:
      return null
  }
}

export function WhatsAppMessageBubble({
  chatId,
  msg,
}: {
  chatId: string
  msg: WhatsAppChatMessage
}): JSX.Element {
  const isOutbound = msg.direction === 'outbound'
  // Outbound rows stored by the worker start as 'sending' and end 'sent' or 'failed'.
  const failed = isOutbound && msg.status === 'failed'
  const sending = isOutbound && (msg.status as string) === 'sending'

  // Call-permission replies are events about the thread, not messages in it —
  // shown centred like a system notice rather than as either side's bubble.
  if (msg.message_type === 'call_permission') {
    return (
      <div className="my-1 flex justify-center">
        <span className="flex items-center gap-1.5 rounded-full bg-[#25D366]/10 px-3 py-1 text-[11px] text-[#075E54]">
          <PhoneCall className="h-3 w-3" />
          {msg.content_text ?? 'Call permission update'}
          <span className="text-gray-400">
            {formatInTimezone(msg.sent_at, { hour: '2-digit', minute: '2-digit' })}
          </span>
        </span>
      </div>
    )
  }

  return (
    <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
      {/* Who replied — outbound only; the inbound side is named in the header. */}
      {isOutbound && msg.agent_name && (
        <span className="mb-0.5 pr-1 text-[10px] font-medium text-gray-500">{msg.agent_name}</span>
      )}
      <div
        className={`relative max-w-[70%] rounded-lg px-2.5 py-1.5 text-sm shadow-sm ${
          failed
            ? 'border border-red-200 bg-red-50 text-gray-700'
            : isOutbound
              ? 'bg-[#D9FDD3] text-gray-800'
              : 'bg-white text-gray-800'
        }`}
      >
        {msg.template_name && (
          <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#128C7E]">
            <FileText className="h-3 w-3" />
            {msg.template_name}
          </div>
        )}
        <MediaBody chatId={chatId} msg={msg} />
        {msg.content_text && <p className="whitespace-pre-wrap break-words">{msg.content_text}</p>}
        {!msg.content_text && !msg.media_id && (
          <p className="italic text-gray-400">[{msg.message_type}]</p>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1">
          <span className="text-[10px] text-gray-400">
            {formatInTimezone(msg.sent_at, { hour: '2-digit', minute: '2-digit' })}
          </span>
          {failed && <span className="text-[10px] text-red-500">Not sent</span>}
          {sending && <span className="text-[10px] text-gray-400">Sending…</span>}
          {isOutbound && <MessageTicks status={msg.status} />}
        </div>
        {msg.status === 'failed' && msg.error_message && (
          <p className="mt-0.5 text-[10px] text-red-500">{msg.error_message}</p>
        )}
      </div>
    </div>
  )
}

/** The not-yet-confirmed twin of the bubble above — dimmed, with a spinner. */
export function PendingBubble({
  msg,
  onDismiss,
}: {
  msg: PendingWhatsAppMessage
  onDismiss: (localId: string) => void
}): JSX.Element {
  const failed = msg.status === 'failed'
  return (
    <div className="flex justify-end">
      <div
        className={`relative max-w-[70%] rounded-lg px-2.5 py-1.5 text-sm shadow-sm ${
          failed ? 'border border-red-200 bg-red-50 text-gray-700' : 'bg-[#D9FDD3] text-gray-800 opacity-70'
        }`}
      >
        {msg.media_filename && (
          <div className="mb-1 flex items-center gap-2 rounded-lg bg-black/5 px-2 py-1.5 text-xs">
            <FileText className="h-3.5 w-3.5" />
            <span className="truncate">{msg.media_filename}</span>
          </div>
        )}
        {msg.content_text && <p className="whitespace-pre-wrap break-words">{msg.content_text}</p>}
        <div className="mt-0.5 flex items-center justify-end gap-1">
          <span className="text-[10px] text-gray-400">
            {failed ? 'Not sent' : 'Sending…'}
          </span>
          <MessageTicks status={msg.status} />
          {failed && (
            <button
              onClick={() => onDismiss(msg.localId)}
              className="ml-0.5 text-gray-400 hover:text-gray-600"
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * A voice call as it appears in the thread — direction, outcome and talk time,
 * the way a phone's call log reads. Rendered centred like a system event, since
 * a call belongs to the conversation rather than to either side's messages.
 */
export function WhatsAppCallEntry({ call }: { call: WhatsAppCall }): JSX.Element {
  const inbound = call.direction === 'inbound'
  const connected = call.duration_seconds != null && call.duration_seconds > 0

  const talkTime = ((): string => {
    const secs = call.duration_seconds ?? 0
    if (secs <= 0) return ''
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  })()

  // "Missed"/"Declined" matter more than the raw status string to an agent
  // scanning the thread, so the outcome is phrased rather than echoed.
  const outcome = connected
    ? `${inbound ? 'Incoming' : 'Outgoing'} call · ${talkTime}`
    : call.status === 'rejected'
      ? `${inbound ? 'Declined' : 'Call declined'}`
      : call.status === 'failed'
        ? 'Call failed'
        : inbound
          ? 'Missed call'
          : 'No answer'

  const tone = connected
    ? 'bg-[#25D366]/10 text-[#075E54]'
    : 'bg-red-50 text-red-600'

  return (
    <div className="my-1 flex justify-center">
      <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] ${tone}`}>
        {inbound ? <PhoneIncoming className="h-3 w-3" /> : <PhoneOutgoing className="h-3 w-3" />}
        {outcome}
        <span className="text-gray-400">
          {formatInTimezone(call.started_at, { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    </div>
  )
}
